import { Button, IconButton, Stack, Typography } from '@mui/material';
import Grid from '@mui/material/Grid2';
import CloseIcon from '@mui/icons-material/Close';
import { useFormContext } from '../../contexts/FormContext';
import { useFormData } from '../../hooks/useFormValues';
import { compare_array_dates } from '../../utils/utils';
import FieldRenderer from './FieldRenderer';
import type { FieldSchema } from './types';

type ListItem = Record<string, unknown>;

const ListField = ({ field }: { field: FieldSchema }) => {
  const { getFormDataItem, handleChange } = useFormContext();
  const formData = useFormData();
  const isDisabled = Object.prototype.hasOwnProperty.call(field, 'disabled')
    ? !!getFormDataItem(formData, field.disabled as string)
    : false;

  const draftName = (subName: string) => `${field.name}__draft__${subName}`;

  return (
    <Grid size={field.size || 12} sx={{ mb: 2 }}>
      <Grid container spacing={2}>
        {field?.fields?.map((subField, index) => (
          <FieldRenderer key={subField.name || index} field={{ ...subField, name: draftName(String(subField.name)) }} />
        ))}
        <Grid>
          <Stack direction="row" spacing={2}>
            <Button
              variant="contained"
              onClick={() => {
                let val;
                const new_element: Record<string, unknown> = {};
                field.fields?.forEach((f) => {
                  new_element[String(f.name)] = getFormDataItem(formData, draftName(String(f.name)));
                });
                val = [new_element];
                if (getFormDataItem(formData, field.name!)) {
                  val = getFormDataItem<ListItem[]>(formData, field.name!);
                  val.push(new_element);
                }
                return handleChange({ target: { name: field.name!, value: val, type: field.type, checked: false } }, field);
              }}
              disabled={isDisabled}
            >
              Add
            </Button>
            <Button
              variant="contained"
              color="error"
              onClick={() => {
                handleChange({ target: { name: field.name!, value: [], type: field.type, checked: false } }, field);
              }}
              disabled={isDisabled}
            >
              Clear
            </Button>
          </Stack>
        </Grid>
      </Grid>
      {getFormDataItem<ListItem[] | undefined>(formData, field.name!)?.length ? (
        <Grid container spacing={2} sx={{ mt: 2 }}>
          <ul style={{ listStyle: 'none', padding: 0, width: '100%' }}>
            {getFormDataItem<ListItem[] | undefined>(formData, field.name!)
              ?.sort(compare_array_dates)
              .map((item, index: number) => {
                return Object.keys(item).map((key) => {
                  const d = new Date(item[key] as string);
                  return (
                    <li
                      key={`${index}-${key}`}
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        padding: '8px 0',
                        borderBottom: '1px solid rgba(0, 0, 0, 0.12)',
                      }}
                    >
                      <Typography color="text.primary">
                        {key}:{' '}
                        {field?.fields?.[0]?.format?.includes('YYYY')
                          ? d.getFullYear() + '-' + ('0' + (d.getMonth() + 1)).slice(-2) + '-' + ('0' + d.getDate()).slice(-2)
                          : d.getDate() + '/' + ('0' + (d.getMonth() + 1)).slice(-2)}
                      </Typography>
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          const val = getFormDataItem<ListItem[]>(formData, field.name!);
                          val.splice(index, 1);
                          return handleChange({ target: { name: field.name!, value: val, type: field.type, checked: false } }, field);
                        }}
                        sx={{
                          color: 'error.main',
                          '&:hover': {
                            backgroundColor: 'error.light',
                            color: 'error.contrastText',
                          },
                        }}
                      >
                        <CloseIcon fontSize="small" />
                      </IconButton>
                    </li>
                  );
                });
              })}
          </ul>
        </Grid>
      ) : null}
    </Grid>
  );
};

export default ListField;
